import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { ArrowRight, Sparkles, Users, Presentation as PresentationIcon, CircleCheck } from "lucide-react";
import ClientShell from "./ClientShell";
import { JourneyRail } from "./JourneyRail";
import { WhyThisButton } from "./shared/WhyThisExplanation";
import { useWorkspaceHome } from "../../lib/rehearsalRoomQueries";
import { useMarkCommitmentDone } from "../../lib/practiceQueries";

const PROGRAMME_LABELS = { presentation: "Presentation", executive_presence: "Executive Presence" };

export default function WorkspaceHome() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { data, isError } = useWorkspaceHome(workspaceId);

  if (isError) return <Navigate to="/rehearsal-room" replace />;
  // NOTE: `!data` (not `isLoading`) is the correct guard here — the home
  // query stays `enabled: false` (isLoading=false, data=undefined) until
  // the internal `useMe()` query resolves, so isLoading alone would miss
  // that window and crash on `data.programme_type` below.
  if (!data) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-paper" data-testid="workspace-home-loading">
        <p className="text-ink-soft">Loading workspace…</p>
      </section>
    );
  }

  return (
    <ClientShell>
      <div className="max-w-3xl" data-testid="workspace-home">
        <Badge variant="outline" className="uppercase text-xs tracking-wide mb-3">
          {PROGRAMME_LABELS[data.programme_type] || data.programme_type}
        </Badge>
        <h1 className="font-serif text-3xl text-ink mb-1" data-testid="workspace-home-title">{data.title}</h1>
        {data.viewer_workspace_role === "coach" ? (
          <p className="text-ink-soft mb-8" data-testid="workspace-home-coach-label">Workspace coach: You</p>
        ) : (
          data.coach_display_name && (
            <p className="text-ink-soft mb-8" data-testid="workspace-home-coach-label">Coach: {data.coach_display_name}</p>
          )
        )}

        {data.action_cards?.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4 mb-8" data-testid="workspace-home-action-cards">
            {data.action_cards.map((card) => <ActionCard key={card.key} card={card} workspaceId={workspaceId} navigate={navigate} />)}
          </div>
        )}

        {data.session_join_window && <SessionJoinWindowCard card={data.session_join_window} workspaceId={workspaceId} navigate={navigate} />}

        {data.review_invitation?.should_show && data.viewer_workspace_role !== "coach" && (
          <ReviewInvitationCard workspaceId={workspaceId} navigate={navigate} />
        )}

        <ProgrammeCard card={data.programme_card} navigate={navigate} workspaceId={workspaceId} />

        {data.recommended_next_practice && data.viewer_workspace_role !== "coach" && (
          <RecommendedNextPracticeCard card={data.recommended_next_practice} workspaceId={workspaceId} navigate={navigate} />
        )}

        {data.open_commitment && data.viewer_workspace_role !== "coach" && (
          <OpenCommitmentCard commitment={data.open_commitment} workspaceId={workspaceId} />
        )}

        <button
          onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/work-documents`)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-terracotta hover:text-terracotta/80 mb-8"
          data-testid="workspace-home-work-documents-link"
        >
          Open Work Documents <ArrowRight size={14} />
        </button>

        <div className="border border-ink/15 bg-white/40 rounded-xl p-6">
          <p className="text-xs uppercase tracking-wide text-terracotta font-medium mb-4">Your journey</p>
          <JourneyRail
            nodes={data.journey_rail}
            onNodeClick={(node) => navigate(`/rehearsal-room/w/${workspaceId}/practice/${node.stage_key}?returnTo=home`)}
            emptyLabel="Your coach hasn't published a journey for this Workspace yet."
            testIdPrefix="workspace-home-journey-rail"
          />
        </div>
      </div>
    </ClientShell>
  );
}

// A card is only ever rendered when the server sent a genuine item — see
// home_view_repository.build_action_cards(). room_launch.show_cta is the
// ONLY signal that decides whether the common Room CTA renders; nothing
// here infers it from any other field.
function ActionCard({ card, workspaceId, navigate }) {
  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-5" data-testid={`workspace-home-action-card-${card.key}`}>
      <p className="font-serif text-lg text-ink mb-1">{card.title}</p>
      {card.subtitle && <p className="text-sm text-ink-soft mb-4">{card.subtitle}</p>}
      {card.room_launch?.show_cta && (
        <button
          onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/room/${card.room_launch.launch.launch_id}`)}
          className="inline-flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta/80"
          data-testid={`workspace-home-room-launch-${card.key}`}
        >
          Go to the Rehearsal Room <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}

// A clear, dismissible Home invitation — shown only when the member has
// reached programme completion / the last active journey stage (see
// review_repository.is_programme_complete_for_member). The subtle
// persistent "Review programme & coach" action in ClientShell is always
// available regardless of this card's visibility.
function ReviewInvitationCard({ workspaceId, navigate }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div
      className="border border-mustard-dark/40 bg-mustard/10 rounded-lg p-5 mb-8 flex flex-wrap items-center justify-between gap-4"
      data-testid="workspace-home-review-invitation"
    >
      <div>
        <p className="font-serif text-lg text-ink">You've reached the end of your journey</p>
        <p className="text-sm text-ink-soft">We'd love to hear how the programme and your coach helped you.</p>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <button
          onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/review`)}
          className="text-sm font-medium text-terracotta hover:text-terracotta/80"
          data-testid="workspace-home-review-invitation-cta"
        >
          Review your programme &amp; coach
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="text-sm text-ink-soft hover:text-ink"
          data-testid="workspace-home-review-invitation-dismiss"
        >
          Not now
        </button>
      </div>
    </div>
  );
}

function SessionJoinWindowCard({ card, workspaceId, navigate }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div
      className="border border-terracotta/30 bg-terracotta/5 rounded-lg p-5 mb-8 flex flex-wrap items-center justify-between gap-4"
      data-testid="workspace-home-session-join-window"
    >
      <p className="font-serif text-lg text-ink">
        {card.starts_in_minutes > 0 ? `Your session starts in ${card.starts_in_minutes} minutes` : "Your session is live now"}
      </p>
      <div className="flex items-center gap-4 shrink-0">
        {card.room_launch?.show_cta && (
          <button
            onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/room/${card.room_launch.launch.launch_id}`)}
            className="text-sm font-medium text-terracotta hover:text-terracotta/80"
            data-testid="workspace-home-session-join-cta"
          >
            Go to the Rehearsal Room
          </button>
        )}
        <button
          onClick={() => setDismissed(true)}
          className="text-sm text-ink-soft hover:text-ink"
          data-testid="workspace-home-session-join-dismiss"
        >
          Not now
        </button>
      </div>
    </div>
  );
}

function ProgrammeCard({ card, navigate, workspaceId }) {
  if (!card) return null;
  if (card.kind === "presentation") {
    return (
      <div className="border border-ink/15 bg-white/50 rounded-lg p-6 mb-8" data-testid="workspace-home-programme-card-presentation">
        <div className="flex items-center gap-2 text-ink-soft mb-3">
          <PresentationIcon size={16} /><span className="text-sm uppercase tracking-wide">Your presentation</span>
        </div>
        {card.current_presentation_title ? (
          <>
            <button
              onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/presentation/${card.current_presentation_id}`)}
              className="font-serif text-xl text-ink hover:text-terracotta text-left block mb-2"
              data-testid="workspace-home-current-presentation-link"
            >
              {card.current_presentation_title}
            </button>
            {typeof card.completion_percent === "number" && (
              <div className="mb-3">
                <Progress value={card.completion_percent} className="h-1.5" />
                <p className="text-xs text-ink-soft mt-1">{card.completion_percent}% completion</p>
              </div>
            )}
          </>
        ) : (
          <p className="text-ink-soft">Your coach hasn't set up your presentation yet.</p>
        )}
        {card.other_presentations_count > 0 && (
          <button
            onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/presentation`)}
            className="text-sm text-terracotta hover:text-terracotta/80 mt-3"
            data-testid="workspace-home-other-presentations-link"
          >
            View {card.other_presentations_count} other presentation{card.other_presentations_count === 1 ? "" : "s"}
          </button>
        )}
      </div>
    );
  }
  if (card.kind === "executive_presence") {
    return (
      <div className="border border-ink/15 bg-white/50 rounded-lg p-6 mb-8" data-testid="workspace-home-programme-card-executive-presence">
        <div className="flex items-center gap-2 text-ink-soft mb-3">
          <Sparkles size={16} /><span className="text-sm uppercase tracking-wide">Current focus</span>
        </div>
        {card.current_focus_label ? (
          <>
            <p className="font-serif text-xl text-ink mb-2">{card.current_focus_label}</p>
            {card.current_application_prompt && <p className="text-ink-soft text-sm mb-3">{card.current_application_prompt}</p>}
          </>
        ) : (
          <p className="text-ink-soft mb-3">Your coach will set your first focus area soon.</p>
        )}
        <button
          onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/presence`)}
          className="text-sm font-medium text-terracotta hover:text-terracotta/80"
          data-testid="workspace-home-presence-lab-link"
        >
          Open Presence Lab
        </button>
      </div>
    );
  }
  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-6 mb-8" data-testid="workspace-home-programme-card-team">
      <div className="flex items-center gap-2 text-ink-soft mb-3">
        <Users size={16} /><span className="text-sm uppercase tracking-wide">Team &amp; your work</span>
      </div>
      <p className="text-ink-soft mb-2" data-testid="workspace-home-team-shared-summary">{card.shared_summary || "Shared team progress will appear here soon."}</p>
      <p className="text-ink-soft text-sm mb-3" data-testid="workspace-home-team-private-next-action">{card.member_private_next_action || "Your next private action will appear here soon."}</p>
      <div className="flex gap-4">
        <button
          onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/our-work`)}
          className="text-sm font-medium text-terracotta hover:text-terracotta/80"
          data-testid="workspace-home-our-work-link"
        >
          Open Our Work
        </button>
        <button
          onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/my-work`)}
          className="text-sm font-medium text-terracotta hover:text-terracotta/80"
          data-testid="workspace-home-my-work-link"
        >
          Open My Work
        </button>
      </div>
    </div>
  );
}

/** Master v5 Prompt 23 — "Recommended next practice". Only ever renders
 * a `published` pick or the honest `no_eligible_item` calm state —
 * never a `pending_coach_review` one (the backend already never
 * returns those here). */
function RecommendedNextPracticeCard({ card, workspaceId, navigate }) {
  return (
    <div className="border border-terracotta/30 bg-terracotta/5 rounded-lg p-6 mb-8" data-testid="workspace-home-recommended-next-practice">
      <div className="flex items-center gap-2 text-terracotta mb-3">
        <Sparkles size={16} /><span className="text-sm uppercase tracking-wide">Recommended next practice</span>
      </div>
      {card.status === "no_eligible_item" ? (
        <p className="text-ink-soft" data-testid="workspace-home-recommendation-no-match">Your coach is preparing your next practice item.</p>
      ) : (
        <>
          <p className="font-serif text-xl text-ink mb-1" data-testid="workspace-home-recommendation-title">{card.library_title || "Next practice"}</p>
          <p className="text-xs text-ink-soft uppercase tracking-wide mb-3">{card.layer_label}</p>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/practice/${card.layer_key}`)}
              className="text-sm font-medium text-terracotta hover:text-terracotta/80"
              data-testid="workspace-home-recommendation-open-link"
            >
              Open this practice
            </button>
            {card.rec_id && <WhyThisButton workspaceId={workspaceId} recId={card.rec_id} />}
          </div>
        </>
      )}
    </div>
  );
}

/** Master v5 Prompt 23 — the closed loop's final surface: the member's
 * one open Commitment. "Mark as done" closes it via the existing
 * Commitment API. */
function OpenCommitmentCard({ commitment, workspaceId }) {
  const markDone = useMarkCommitmentDone(workspaceId);
  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-6 mb-8 flex items-start justify-between gap-4" data-testid="workspace-home-open-commitment">
      <div>
        <div className="flex items-center gap-2 text-ink-soft mb-2">
          <CircleCheck size={16} /><span className="text-sm uppercase tracking-wide">Your commitment</span>
        </div>
        <p className="text-ink" data-testid="workspace-home-commitment-text">{commitment.text}</p>
      </div>
      <button
        onClick={() => markDone.mutate(commitment.cmt_id)}
        disabled={markDone.isPending}
        className="shrink-0 text-sm font-medium text-terracotta hover:text-terracotta/80 disabled:opacity-50"
        data-testid="workspace-home-commitment-done-btn"
      >
        {markDone.isPending ? "Marking…" : "Mark as done"}
      </button>
    </div>
  );
}

